import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { type UpdateUserSettings } from '../types/types'
import { throwOnError } from '../utils/throwOnError'

export const useGetUserByIdQuery = (userId?: string) => {
  const { authFetch } = useAuth()
  return useQuery({
    staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    queryKey: ['users', userId],
    queryFn:
      userId != null && userId.length > 0
        ? async () => {
            const { data, error } = await authFetch().GET('/users/{userId}', {
              params: {
                path: {
                  userId
                }
              }
            })
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

export const useGetUserSettingsByIdUsername = (
  username: string | undefined
) => {
  const auth = useAuth()
  const authFetch = auth?.authFetch
  return useQuery({
    queryKey: ['userSettings'],
    queryFn:
      username != null && username.length > 0
        ? async () => {
            const { data, error } = await authFetch().GET(
              '/users/{userId}/settings',
              {
                params: {
                  path: {
                    userId: username
                  }
                }
              }
            )
            if (error) {
              throwOnError(error)
            }
            return data
          }
        : skipToken
  })
}

interface UpdateUserSettingsMutation {
  username: string
  body: UpdateUserSettings
}

export const useUpdateUserSettingsMutation = () => {
  const auth = useAuth()
  const authFetch = auth?.authFetch
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ username, body }: UpdateUserSettingsMutation) => {
      const { data: existingUserSettings, error: errorGet } =
        await authFetch().GET('/users/{userId}/settings', {
          params: {
            path: {
              userId: username
            }
          }
        })
      if (errorGet) {
        throwOnError(errorGet)
      }
      const parsedBody = JSON.parse(body.settings)
      const parsedExistingUserSettings = JSON.parse(
        existingUserSettings.settings
      )
      const newUserSettings = {
        ...parsedExistingUserSettings,
        ...parsedBody
      }
      if (
        parsedExistingUserSettings.betaFeaturesEnabled != null ||
        parsedBody.betaFeaturesEnabled != null
      ) {
        newUserSettings.betaFeaturesEnabled = {
          ...parsedExistingUserSettings.betaFeaturesEnabled,
          ...parsedBody.betaFeaturesEnabled
        }
      }
      const { data: updatedUserSettings, error: errorPut } =
        await authFetch().PUT('/users/{userId}/settings', {
          params: {
            path: { userId: username }
          },
          body: { settings: JSON.stringify(newUserSettings) }
        })
      if (errorPut) {
        throwOnError(errorPut)
      }
      return updatedUserSettings
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['userSettings']
      })
    }
  })
}
